import { useNavigate } from 'react-router-dom'
import { ArrowRight, BookOpenCheck } from 'lucide-react'
import { useLocale } from '../../i18n/context'

// Magnitude fill for the weak-spot bar (DESIGN_SYSTEM "Wave 3" §1/§3): a single
// low-saturation ramp, NEVER red. A weak spot is a PRIORITY, not an error —
// emphasis comes from ordering (lowest first) and the bar's fill length, not from
// an alarming hue. Diverges intentionally from VocabularyMap's rainbow, which
// migrates to the magnitude model in its own pass.
function masteryFill(pct: number): string {
  if (pct < 30) return 'var(--mastery-fill-low)'
  if (pct < 55) return 'var(--mastery-fill-mid)'
  return 'var(--mastery-fill-high)'
}

// Format topic string identically to VocabularyMap (Title Case, split on _ - space).
function formatTopic(topic: string): string {
  return topic
    .split(/[\s_-]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
}

// ── Public prop types ─────────────────────────────────────────────────────────

export interface WeakSpotItem {
  topicName: string
  masteryPercent: number
  deckId: string
}

export interface WeakSpotsProps {
  items?: WeakSpotItem[] | null
  /** Topic currently being launched (shows spinner on that row's Train button) */
  pendingTopic?: string | null
  /** Called when Train is pressed for a topic-only item (no deckId). */
  onTrainTopic?: (topicName: string) => void
  /** A topic whose training had nothing to practise (ADR-112: topic training takes
   *  only words already answered). Explained under the list, in place of a session. */
  nothingToPractiseTopic?: string | null
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      className="flex items-center gap-3 px-4 py-4 rounded-xl"
      style={{
        background: 'var(--bg-secondary)',
        border: '1.5px dashed var(--border)',
      }}
    >
      <BookOpenCheck
        size={18}
        strokeWidth={1.5}
        style={{ color: 'var(--text-secondary)', flexShrink: 0 }}
      />
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        Complete your first session to see weak spots.
      </p>
    </div>
  )
}

// ── Single row ────────────────────────────────────────────────────────────────

interface RowProps {
  item: WeakSpotItem
  isLast: boolean
  onTrain: () => void
  isPending?: boolean
}

function Row({ item, isLast, onTrain, isPending }: RowProps) {
  const { tr } = useLocale()
  const prog = tr.progress
  const { topicName, masteryPercent } = item
  const pct = Math.max(0, Math.min(100, masteryPercent))
  const fill = masteryFill(pct)
  const label = formatTopic(topicName)

  return (
    <div
      className="flex items-center gap-3"
      style={{
        minHeight: 60,
        padding: '10px 16px',
        borderBottom: isLast ? 'none' : '1px solid var(--border)',
      }}
    >
      {/* Magnitude dot indicator — magnitude-ramp hue, never red */}
      <div
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: fill,
          flexShrink: 0,
        }}
      />

      {/* Topic name */}
      <span
        className="text-sm font-medium truncate"
        style={{
          color: 'var(--text-primary)',
          flex: '1 1 0',
          minWidth: 0,
          fontFamily: 'var(--font-ui)',
        }}
        title={label}
      >
        {label}
      </span>

      {/* Mini progress bar + percentage */}
      <div
        className="flex items-center gap-2 flex-shrink-0"
        style={{ minWidth: 80 }}
      >
        {/* Track */}
        <div
          style={{
            flex: 1,
            height: 4,
            backgroundColor: 'var(--mastery-track)',
            borderRadius: 2,
            overflow: 'hidden',
            minWidth: 44,
          }}
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={prog.trainTopic(label)}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              backgroundColor: fill,
              borderRadius: 2,
              // Smooth re-render when parent swaps data
              transition: 'width 0.4s ease',
            }}
          />
        </div>

        {/* Percentage label — neutral framing (§3): the number is informational,
            not an alarm, so it is not tinted by the magnitude hue. */}
        <span
          className="tabular-nums text-xs font-semibold"
          style={{ color: 'var(--text-secondary)', minWidth: 32, textAlign: 'right', fontFamily: 'var(--font-ui)' }}
        >
          {Math.round(pct)}%
        </span>
      </div>

      {/* Train button */}
      <button
        onClick={onTrain}
        disabled={isPending}
        className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full
                   transition-opacity hover:opacity-75 active:scale-95 flex-shrink-0
                   disabled:opacity-60 disabled:cursor-not-allowed"
        style={{
          background: 'var(--accent-soft)',
          color: 'var(--accent)',
          minHeight: 32,
          minWidth: 60,
          justifyContent: 'center',
        }}
        aria-label={isPending ? prog.loadingTopic(label) : prog.trainTopic(label)}
      >
        {isPending ? (
          <span className="animate-pulse">…</span>
        ) : (
          <>{prog.train}<ArrowRight size={11} strokeWidth={2.5} /></>
        )}
      </button>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export default function WeakSpots({ items, pendingTopic, onTrainTopic, nothingToPractiseTopic }: WeakSpotsProps) {
  const navigate = useNavigate()
  const { tr } = useLocale()

  if (!items || items.length === 0) {
    return <EmptyState />
  }

  // Guard: show at most 3, lowest mastery first
  const displayed = [...items]
    .sort((a, b) => a.masteryPercent - b.masteryPercent)
    .slice(0, 3)

  function getOnTrain(item: WeakSpotItem) {
    if (item.deckId) {
      return () => navigate(`/decks/${item.deckId}/learn`, { state: { from: '/home' } })
    }
    if (onTrainTopic) {
      return () => onTrainTopic(item.topicName)
    }
    return () => navigate('/learn', { state: { from: '/home' } })
  }

  return (
    <div className="flex flex-col gap-2">
      <div
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
        }}
      >
        {displayed.map((item, idx) => (
          <Row
            key={item.deckId + item.topicName}
            item={item}
            isLast={idx === displayed.length - 1}
            isPending={pendingTopic === item.topicName}
            onTrain={getOnTrain(item)}
          />
        ))}
      </div>
      {nothingToPractiseTopic && (
        <p role="status" className="text-sm" style={{ color: 'var(--text-secondary)', margin: 0 }}>
          {tr.home.topicNothingToPractise(formatTopic(nothingToPractiseTopic))}
        </p>
      )}
    </div>
  )
}
